import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";
import classnames from "classnames";

import {
  fetchCharts,
  fetchStatistics,
  setEventsPeriod,
} from "../../store/actions/events.actions";
import { useTypedSelector } from "../../store/models/root.interface";

import { CombinedEventStatistics, EventsPeriod } from "../../api";

import StatisticBox from "../../common/components/StatisticBox";
import DateRange from "../../common/components/DateRange";
import Loader from "../../common/components/Loader";
import TitleMeta from "../../common/components/TitleMeta";

import Layout from "../../components/Layout/Layout";
import MostPopular from "../../components/Events/MostPopular";
import EventsCalendar from "../../components/Events/EventsCalendar";
import EventsDiary from "../../components/Events/EventsDiary";

const EventsOverview: React.FC = () => {
  const dispatch = useDispatch();
  const period = useTypedSelector((state) => state.event.period);

  const statistics = useTypedSelector(
    (state) => state.event.statisticsByPeriod
  );

  const data = useMemo(() => {
    const state =
      typeof statistics[period] !== "string"
        ? (statistics[period] as CombinedEventStatistics)
        : void 0;
    return [
      {
        title: "Total Attendees",
        value: state?.attendance?.total,
        results: [
          {
            title: "Unique",
            value: state?.attendance?.unique,
          },
          {
            title: "Repeat",
            value: state?.attendance?.repeat,
          },
        ],
      },
      {
        title: "Total Events",
        value: state?.events?.total,
        results: [
          {
            title: "Previous",
            value: state?.events?.previous,
          },
          {
            title: "Upcoming",
            value: state?.events?.upcoming,
          },
        ],
      },
      {
        title: "Satisfaction Score",
        value: state ? state.scores?.overall ?? "-" : void 0,
        description: state?.scores ? "average" : "None available",
        isPercent: !!state?.scores,
        results: [
          {
            title: "Avg. Content",
            value: state ? state.scores?.content ?? "-" : void 0,
            isPercent: !!state?.scores,
          },
          {
            title: "Avg. Presenter",
            value: state ? state.scores?.presenter ?? "-" : void 0,
            isPercent: !!state?.scores,
          },
        ],
      },
    ];
  }, [statistics, period]);

  const handleSelectDateRange = (period: EventsPeriod) => {
    dispatch(setEventsPeriod(period));
    dispatch(fetchStatistics(period));
    dispatch(fetchCharts(period));
  };

  useEffect(() => {
    dispatch(fetchStatistics(period));
    dispatch(fetchCharts(period));
  }, []);

  return (
    <>
      <TitleMeta title="Events" />

      <Layout.SidebarPage>

        <Layout.Main>

          <Layout.Header>
            <Layout.Breadcrumb />
            <div className="d-flex align-items-center">
              <Layout.Title>Events</Layout.Title>
              <div>
                <DateRange
                  onChange={(value) => handleSelectDateRange(value)}
                  value={period}
                />
              </div>
            </div>
          </Layout.Header>

          <Layout.Body>

            <Row>
              {data.map((x, i) => (
                <Col
                  xs={12}
                  sm={4}
                  key={i}
                  className={classnames({
                    "mt-3": i !== 0,
                    "mt-sm-0": true,
                  })}
                >
                  <div className="position-relative">
                    <StatisticBox {...x} />
                    <Loader show={statistics[period] === "loading"} />
                  </div>
                </Col>
              ))}
            </Row>

            <Row>
              <MostPopular />
            </Row>

          </Layout.Body>

        </Layout.Main>

        <Layout.Sidebar>
          <EventsCalendar />
          <EventsDiary />
        </Layout.Sidebar>

      </Layout.SidebarPage>
    </>
  );
};

export default EventsOverview;
